import React, { useRef, useState } from 'react';
import {
  Box,
  Stack,
  Typography,
  IconButton,
  Tooltip,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import EmailIcon from '@mui/icons-material/Email';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/authContext';
import LockIconWrapper from '../components/LockIconWrapper';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Shared voucher email block: shows the stored buyer email and, gated by the
// respective rights, lets staff edit the address and re-trigger the voucher
// email. Used by both the value voucher and the resource (product) voucher
// details modals.
function VoucherEmailSection({
  email,
  status,
  onResend,
  onEdit,
  onEmailUpdated,
  resendRight = 'voucher:special:resendEmail',
  editRight = 'voucher:special:editBuyerEmail',
}) {
  const { checkRightAny } = useAuth();
  const emailInputRef = useRef(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [resending, setResending] = useState(false);

  const canEdit = checkRightAny(editRight);
  const canResend = checkRightAny(resendRight);
  const isWaitingForPayment = status === 'waitingForPayment';

  const resendDisabledReason = !canResend
    ? 'Keine Berechtigung zum erneuten Versenden'
    : !email
      ? 'Keine E-Mail-Adresse hinterlegt'
      : isWaitingForPayment
        ? 'Gutschein muss erst bezahlt werden'
        : 'Gutschein-E-Mail erneut versenden';

  const handleResend = async () => {
    if (!onResend) return;
    setResending(true);
    try {
      // useHttp returns null when the request was deferred for rights escalation;
      // the stored request runs after re-auth, so skip the premature success toast.
      const result = await onResend();
      if (result === null) return;
      toast.success('Gutschein-E-Mail wurde erneut versendet');
    } catch (err) {
      const code = err?.code || err?.response?.data?.code;
      if (code === 'VOUCHER_NO_BUYER_EMAIL' || code === 'RESOURCE_VOUCHER_NO_BUYER_EMAIL') {
        toast.error('Keine E-Mail-Adresse hinterlegt');
      } else {
        toast.error('Fehler beim erneuten Versenden der Gutschein-E-Mail');
      }
      console.error('Resend voucher email failed:', err);
    } finally {
      setResending(false);
    }
  };

  const handleSaveEmail = async () => {
    const nextEmail = emailInputRef.current?.value?.trim() || '';
    if (!EMAIL_REGEX.test(nextEmail)) {
      toast.error('Bitte eine gültige E-Mail-Adresse eingeben');
      return;
    }
    setEditSaving(true);
    try {
      // useHttp returns null when the request was deferred for rights escalation.
      const result = await onEdit(nextEmail);
      if (result === null) {
        setEditOpen(false);
        return;
      }
      toast.success('E-Mail-Adresse wurde aktualisiert');
      if (onEmailUpdated) onEmailUpdated(nextEmail);
      setEditOpen(false);
    } catch (err) {
      toast.error('Fehler beim Aktualisieren der E-Mail-Adresse');
      console.error('Update voucher buyer email failed:', err);
    } finally {
      setEditSaving(false);
    }
  };

  return (
    <>
      <Box>
        <Stack direction='row' alignItems='center' spacing={1} flexWrap='wrap'>
          <Typography variant='body2'>
            <strong>Käufer E-Mail:</strong> {email || 'nicht hinterlegt'}
          </Typography>
          {canEdit && (
            <Tooltip title='E-Mail-Adresse bearbeiten'>
              <IconButton
                size='small'
                onClick={() => setEditOpen(true)}
                aria-label='E-Mail-Adresse bearbeiten'
              >
                <LockIconWrapper right={editRight}>
                  <EditIcon fontSize='small' />
                </LockIconWrapper>
              </IconButton>
            </Tooltip>
          )}
        </Stack>

        <Box sx={{ mt: 1 }}>
          <Tooltip title={resendDisabledReason}>
            <span>
              <Button
                variant='outlined'
                size='small'
                startIcon={resending ? <CircularProgress size={16} /> : <EmailIcon />}
                onClick={handleResend}
                disabled={!canResend || !email || isWaitingForPayment || resending}
              >
                <LockIconWrapper right={resendRight}>E-Mail erneut senden</LockIconWrapper>
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Box>

      <Dialog open={editOpen} onClose={() => (editSaving ? null : setEditOpen(false))} fullWidth>
        <DialogTitle>Käufer E-Mail-Adresse bearbeiten</DialogTitle>
        <DialogContent>
          <TextField
            inputRef={emailInputRef}
            defaultValue={email || ''}
            type='email'
            label='E-Mail-Adresse'
            fullWidth
            margin='dense'
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)} disabled={editSaving}>
            Abbrechen
          </Button>
          <Button
            variant='contained'
            onClick={handleSaveEmail}
            disabled={editSaving}
            startIcon={editSaving ? <CircularProgress size={16} /> : null}
          >
            Speichern
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default VoucherEmailSection;
